/**
 * Organism · Navbar — behaviour for the markup contract documented in
 * navbar.scss. The markup is authored statically in each page so the chrome
 * paints on first frame; this only attaches state.
 *
 * Call once per page:
 *
 *     initNavbar();
 */

const SCROLL_THRESHOLD = 8;

/**
 * Reads a breakpoint from the token layer rather than restating it here.
 * `--fc-bp-*` is emitted by _tokens.scss from the same Sass map the @media
 * queries use, so the panel's behaviour and the CSS that hides it can never
 * disagree about where the navbar collapses.
 */
function breakpoint(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--fc-bp-${name}`)
    .trim();
  return value || fallback;
}

export function initNavbar(root?: HTMLElement | null): () => void {
  const navbar = root ?? document.querySelector<HTMLElement>('[data-fc-navbar]');
  if (!navbar) return () => {};

  const toggle = navbar.querySelector<HTMLButtonElement>('.fc-navbar__toggle');
  const menu = navbar.querySelector<HTMLElement>('.fc-navbar__menu');
  const cleanups: Array<() => void> = [];

  // ─── Surface state on scroll ───
  const syncScrolled = () => {
    navbar.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
  };
  window.addEventListener('scroll', syncScrolled, { passive: true });
  cleanups.push(() => window.removeEventListener('scroll', syncScrolled));
  syncScrolled();

  // ─── Mobile menu ───
  if (toggle && menu) {
    const setOpen = (open: boolean) => {
      navbar.dataset.open = String(open);
      toggle.setAttribute('aria-expanded', String(open));
    };

    setOpen(false);

    const onToggle = () => setOpen(navbar.dataset.open !== 'true');
    toggle.addEventListener('click', onToggle);
    cleanups.push(() => toggle.removeEventListener('click', onToggle));

    // A menu link is a navigation — collapse behind it, including same-page
    // anchors where no page load would otherwise close the panel.
    const onMenuClick = (event: Event) => {
      if ((event.target as HTMLElement).closest('a')) setOpen(false);
    };
    menu.addEventListener('click', onMenuClick);
    cleanups.push(() => menu.removeEventListener('click', onMenuClick));

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || navbar.dataset.open !== 'true') return;
      setOpen(false);
      toggle.focus();
    };
    document.addEventListener('keydown', onKeydown);
    cleanups.push(() => document.removeEventListener('keydown', onKeydown));

    const onDocumentClick = (event: MouseEvent) => {
      if (navbar.dataset.open !== 'true') return;
      if (!navbar.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDocumentClick);
    cleanups.push(() => document.removeEventListener('click', onDocumentClick));

    // Growing past the breakpoint hides the panel in CSS; clear the state too
    // so the toggle's aria-expanded doesn't lie about a menu nobody can see.
    const mql = window.matchMedia(`(max-width: ${breakpoint('md', '600px')})`);
    const onBreakpoint = (event: MediaQueryListEvent) => {
      if (!event.matches) setOpen(false);
    };
    mql.addEventListener('change', onBreakpoint);
    cleanups.push(() => mql.removeEventListener('change', onBreakpoint));
  }

  return () => cleanups.forEach((fn) => fn());
}
